import { Trans, useLingui } from "@lingui/react/macro";
import { useQueries } from "@tanstack/react-query";
import { ReactNode, useMemo } from "react";

import {
  apiGetHfComorbiditiesByPatientIdOptions,
  apiGetMedicaldataOptions,
  apiGetPatientsByPatientIdAlertsOptions,
  apiGetPatientsByPatientIdEventsOptions,
  apiGetPatientsByPatientIdMedicationsOptions,
  HospitalizationEventReason,
  type PatientAISummary,
  PermissionBit,
} from "../../../api";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { Spinner } from "../../../components/ui-kit/spinner";
import { alertTypes } from "../../../constants/enum-to-text";
import {
  hasMedicalAccess,
  hasPermission,
} from "../../../constants/permissions";
import { getServerTodayISO } from "../../../utils/server-time";
import { dateTimeOrNull } from "../../../utils/utils";
import { eventCategory, eventCategoryDetails } from "../events/enums";
import {
  type AlertsSummary,
  type Comorbidity,
  type Hospitalizations as HospitalizationsFact,
  type LastCall as LastCallFact,
  type Medication,
  NYHA_MEASUREMENT,
  RHYTHM_MEASUREMENT,
  summaryFacts,
  type SummaryFacts,
} from "./summary-facts";

/**
 * A patient's AI summary: the heading and reasoning a model wrote, with the
 * clinical facts they were drawn from between them. A waiting-room row carries
 * none of those facts, so all of them are read here.
 */
export function AiSummaryContent(props: {
  patientId: number;
  permissions: PermissionBit[];
  summary?: PatientAISummary | null;
}) {
  const heading = props.summary?.heading?.trim();
  const reasoning = props.summary?.reasoning?.trim();
  return (
    <>
      {heading && <div className="text-sm font-semibold">{heading}</div>}
      <AiSummaryFacts
        patientId={props.patientId}
        permissions={props.permissions}
      />
      {reasoning && (
        <Reasoning text={reasoning} generatedAt={props.summary?.generated_at} />
      )}
    </>
  );
}

/** The label/value grid, then the alerts, comorbidities and medications. */
function AiSummaryFacts(props: {
  patientId: number;
  permissions: PermissionBit[];
}) {
  const { t } = useLingui();
  const { facts, loading } = useSummaryFacts(props);

  if (loading) {
    // About the height the facts take, so the popover opens near its final
    // size rather than growing under the reader once they arrive.
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <div className="@container flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-x-6 gap-y-2 @sm:grid-cols-2 @lg:grid-cols-3">
        <Fact label={t`HF type`} value={facts.hfType} empty={<Unknown />} />
        <Fact
          label={t`NYHA class`}
          value={facts.nyhaClass}
          empty={<Unknown />}
        />
        <Fact
          label={t`ECG rhythm`}
          value={facts.ecgRhythm}
          empty={<Unknown />}
        />
        <Fact
          label={t`Hospitalizations`}
          value={facts.hospitalizations}
          empty={<Trans>No hospitalizations on record</Trans>}
        >
          {(value) => <Hospitalizations value={value} />}
        </Fact>
        <Fact
          label={t`Last call`}
          value={facts.lastCall}
          empty={<Trans>No calls on record</Trans>}
        >
          {(value) => <LastCall value={value} />}
        </Fact>
        <Fact
          label={t`Reported`}
          value={facts.reportedOn}
          empty={<Trans>Not reported</Trans>}
        >
          {(value) => <>{dateTimeOrNull(value, false)}</>}
        </Fact>
      </div>

      <Fact
        label={t`Alerts`}
        value={facts.alerts}
        empty={<Trans>No alerts on record</Trans>}
      >
        {(value) => <Alerts value={value} />}
      </Fact>

      <Fact
        label={t`Comorbidities`}
        value={facts.comorbidities}
        empty={<Trans>No comorbidities recorded</Trans>}
      >
        {(value) => <Comorbidities value={value} />}
      </Fact>

      <Fact
        label={t`Medications`}
        value={facts.medications}
        empty={<Trans>No medications recorded</Trans>}
      >
        {(value) => <>{value.map(medicationText).join(", ")}</>}
      </Fact>
    </div>
  );
}

/**
 * One fact's three states. A source that was not read says so; only a source
 * that was read and holds nothing gets `empty`, which is a clinical statement.
 */
function Fact<T>(props: {
  label: string;
  value: T | null | undefined;
  empty: ReactNode;
  children?: (value: T) => ReactNode;
}) {
  const { value } = props;
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      <span className="text-2xs uppercase text-muted-foreground">
        {props.label}
      </span>
      <span className="min-w-0">
        {value === undefined ? (
          <span className="text-muted-foreground">
            <Trans>not available</Trans>
          </span>
        ) : value === null ? (
          props.empty
        ) : props.children ? (
          props.children(value)
        ) : (
          <>{value}</>
        )}
      </span>
    </div>
  );
}

function Reasoning(props: { text: string; generatedAt?: string | null }) {
  const date = dateTimeOrNull(props.generatedAt);
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      <span className="text-2xs uppercase text-muted-foreground">
        <Trans>AI reasoning</Trans>
        {date && (
          <span className="ml-1 normal-case">
            <Trans>generated {date}</Trans>
          </span>
        )}
      </span>
      <p className="whitespace-pre-wrap">{props.text}</p>
    </div>
  );
}

function Unknown() {
  return (
    <span className="text-muted-foreground">
      <Trans>unknown</Trans>
    </span>
  );
}

function medicationText(medication: Medication): string {
  return `${medication.name} ${medication.dose} ${medication.unit}`;
}

function Hospitalizations(props: { value: HospitalizationsFact }) {
  const { value } = props;
  const last = dateTimeOrNull(value.lastDate, false);
  return (
    <>
      {last ? (
        <Trans>
          {value.count}, last {last}
        </Trans>
      ) : (
        value.count
      )}
      {value.heartFailure && (
        <span className="ml-1 text-muted-foreground">
          (
          {resolveEnumLabel(
            eventCategoryDetails[HospitalizationEventReason.hfh],
          )}
          )
        </span>
      )}
    </>
  );
}

function LastCall(props: { value: LastCallFact }) {
  const { value } = props;
  return (
    <>
      {dateTimeOrNull(value.date, false)}
      <span className="ml-1 text-muted-foreground">
        ({resolveEnumLabel(eventCategory[value.category])})
      </span>
    </>
  );
}

function Alerts(props: { value: AlertsSummary }) {
  const { value } = props;
  return (
    <>
      {value.types.map((type) => resolveEnumLabel(alertTypes[type])).join(", ")}
      <span className="ml-1 text-muted-foreground">
        (<Trans>{value.unseen} unseen</Trans>)
      </span>
    </>
  );
}

function Comorbidities(props: { value: Comorbidity[] }) {
  const { t } = useLingui();
  return (
    <>
      {props.value
        .map((c) => (c.inRemission ? `${c.name} (${t`in remission`})` : c.name))
        .join(", ")}
    </>
  );
}

const FACT_READ_STALE_MS = 5 * 60_000;

/**
 * The fact sources, read for one patient: one query per source, so one source
 * failing — or being closed to this caller — leaves its own facts unread
 * rather than empty.
 *
 * The two measurements are asked for by name: `search_string` is a single
 * glob over `measurement_type` with no alternation, and the one route serving
 * both together returns all 63 exam variables, so two narrow reads cost less
 * than one wide one.
 *
 * Each read is fresh for a few minutes, so a second open of the popover is
 * free, unless `markReadsStale()` ran after a write in between. Each read also
 * handles its own failure: the popover says the source was not read.
 */
function useSummaryFacts(options: {
  patientId: number;
  permissions: PermissionBit[];
}): { facts: SummaryFacts; loading: boolean } {
  const { patientId, permissions } = options;
  const medical = hasMedicalAccess(permissions);
  const factRead = {
    staleTime: FACT_READ_STALE_MS,
    meta: { handlesOwnErrors: true },
  };
  const measurementsOf = (search: string) =>
    apiGetMedicaldataOptions({
      query: { patient_ids: [patientId], search_string: search },
    });

  const results = useQueries({
    queries: [
      { ...measurementsOf(NYHA_MEASUREMENT), ...factRead, enabled: medical },
      { ...measurementsOf(RHYTHM_MEASUREMENT), ...factRead, enabled: medical },
      {
        ...apiGetHfComorbiditiesByPatientIdOptions({
          path: { patient_id: patientId },
        }),
        ...factRead,
        enabled: medical,
      },
      {
        ...apiGetPatientsByPatientIdMedicationsOptions({
          path: { patient_id: patientId },
        }),
        ...factRead,
        enabled: medical,
      },
      {
        ...apiGetPatientsByPatientIdEventsOptions({
          path: { patient_id: patientId },
        }),
        ...factRead,
        enabled: hasPermission(permissions, PermissionBit.VIEW_EVENTS),
      },
      {
        ...apiGetPatientsByPatientIdAlertsOptions({
          path: { patient_id: patientId },
        }),
        ...factRead,
        enabled: hasPermission(permissions, PermissionBit.VIEW_ALERTS),
      },
    ],
  });
  const [nyha, rhythm, diagnoses, medications, events, alerts] = results;

  // A disabled query stays `isPending` forever, so the spinner has to watch
  // whether a request is actually in flight — which is what `isLoading` is.
  const loading = results.some((r) => r.isLoading);

  // The alert and event lists are unpaginated, so recomputing the facts on
  // every render of an observer parent would re-sort the patient's whole
  // history each time.
  const facts = useMemo(
    () =>
      summaryFacts({
        // `undefined` here — a failed read, or one this caller may not make —
        // is what tells the popover the source was not read, as opposed to
        // read and holding nothing.
        nyha: nyha.data?.medical_data,
        rhythm: rhythm.data?.medical_data,
        diagnoses: diagnoses.data?.diagnoses,
        medications: medications.data?.medications,
        events: events.data?.events,
        alerts: alerts.data?.alerts,
        today: getServerTodayISO(),
      }),
    [
      nyha.data,
      rhythm.data,
      diagnoses.data,
      medications.data,
      events.data,
      alerts.data,
    ],
  );

  return { facts, loading };
}
